import React, { FC, useEffect, useRef, useState } from 'react';
import classes from './ServicesPage.module.scss';
import ServicesReportTable from '../../../components/Admin/Tables/AnalyticsAndReports/ServicesReportTable/ServicesReportTable';
import HeaderAnalyticsTables from '../../../components/Admin/HeaderAnalyticsAndReports/HeaderAnalyticsTables';
import { useGetServiceReport } from '../../../hooks/Admin/AnalyticsAndReportsTables/ServiceReport/useGetServiceReport';
import { currentDate } from '../../../utils/Admin/AnalyticsAndReports/formatAnalyticAndReportsDate';
import { saveAsCSV } from '../../../utils/Admin/AnalyticsAndReports/saveAsCSV';
import { ServicesHeader } from '../../../types/enums';
import BranchQueueFilter from '../../../components/Admin/BranchQueueFilter/BranchQueueFilter';
import { useAdminQueueIdsFilter } from '../../../hooks/Admin/useAdminQueueIdsFilter';
import ReactSelect, { type StylesConfig } from 'react-select';

const tabsList = ['Период'];
const isActiveButtonCSV = true;

type ServiceOption = { label: string; value: string };

const ServicesPage: FC = () => {
  const [activeTab, setActiveTab] = useState(tabsList[0]);
  const [selectedStartDate, setSelectedStartDate] = useState(currentDate);
  const [selectedEndDate, setSelectedEndDate] = useState(currentDate);
  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);
  const [selectedServiceName, setSelectedServiceName] = useState<string>('');
  const selectedBranchQueueId = filterQueueIds.length ? filterQueueIds[0] : null;
  const isQueueSelected = selectedBranchQueueId != null;
  const { queueIdsFilter } = useAdminQueueIdsFilter();
  const prevQueueIdRef = useRef<number | null>(null);
  const menuPortalTarget = typeof window !== 'undefined' ? document.body : undefined;
  const selectedDate = {
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    branchQueueId: selectedBranchQueueId
  };
  const {
    serviceReportResp: serviceReportData,
    refetchServiceReport,
    isLoadingServiceReport
  } = useGetServiceReport(selectedDate);

  const serviceNames = React.useMemo(() => {
    const unique = new Set<string>();
    (serviceReportData ?? []).forEach((r) => {
      if (r?.serviceName) unique.add(String(r.serviceName));
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [serviceReportData]);

  const serviceOptions: ServiceOption[] = React.useMemo(
    () => serviceNames.map((name) => ({ label: name, value: name })),
    [serviceNames]
  );

  const selectedServiceOption = React.useMemo(
    () => serviceOptions.find((o) => o.value === selectedServiceName) ?? null,
    [serviceOptions, selectedServiceName]
  );

  const filteredServiceReportData = React.useMemo(() => {
    const data = serviceReportData ?? [];
    if (!selectedServiceName) return data;
    return data.filter((r) => String(r.serviceName) === selectedServiceName);
  }, [serviceReportData, selectedServiceName]);

  const selectStyles: StylesConfig<ServiceOption, false> = React.useMemo(
    () => ({
      container: (base) => ({ ...base, width: 250, maxWidth: 250 }),
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        height: 38,
        borderRadius: 8,
        borderColor: state.isFocused ? '#5aa2dc' : 'var(--gray-border)',
        boxShadow: 'none',
        backgroundColor: 'var(--white)',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--font-roboto-400)',
        fontSize: 15,
        ':hover': {
          borderColor: state.isDisabled ? 'var(--gray-border)' : '#5aa2dc'
        }
      }),
      input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 15, lineHeight: '20px' }),
      valueContainer: (base) => ({ ...base, padding: '0 10px' }),
      singleValue: (base) => ({
        ...base,
        color: '#404040',
        fontSize: 15,
        lineHeight: '20px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%'
      }),
      placeholder: (base) => ({ ...base, color: '#404040', fontSize: 15, lineHeight: '20px' }),
      indicatorSeparator: (base) => ({ ...base, display: 'none' }),
      dropdownIndicator: (base) => ({ ...base, padding: 6 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, width: 250, maxWidth: 250, fontSize: 15 }),
      menuList: (base) => ({ ...base, maxHeight: 240, fontSize: 15 }),
      option: (base, state) => ({
        ...base,
        fontSize: 15,
        lineHeight: '20px',
        cursor: 'pointer',
        backgroundColor: state.isFocused ? 'rgba(0, 0, 0, 0.04)' : 'var(--white)',
        color: '#404040'
      })
    }),
    []
  );

  const handleSaveAsCSV = () => {
    const dataWithoutAvg = (filteredServiceReportData ?? []).map(({ averageServiceTime, ...rest }) => rest);
    saveAsCSV(dataWithoutAvg, ServicesHeader);
  };

  useEffect(() => {
    const prev = prevQueueIdRef.current;
    if (prev !== null && selectedBranchQueueId !== null && prev !== selectedBranchQueueId) {
      setSelectedStartDate(currentDate);
      setSelectedEndDate(currentDate);
      setSelectedServiceName('');
    }
    prevQueueIdRef.current = selectedBranchQueueId;
  }, [selectedBranchQueueId]);

  useEffect(() => {
    if (isQueueSelected) {
      refetchServiceReport();
    }
  }, [selectedStartDate, selectedEndDate, selectedBranchQueueId]);

  useEffect(() => {
    // если после смены периода/очереди выбранная услуга отсутствует в данных — сбрасываем выбор
    if (selectedServiceName && !serviceNames.includes(selectedServiceName)) {
      setSelectedServiceName('');
    }
  }, [serviceNames, selectedServiceName]);

  return (
    <div className={classes.container}>
      <HeaderAnalyticsTables
        tabs={tabsList}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleSaveAsCSV={handleSaveAsCSV}
        isActiveButtonCSV={isActiveButtonCSV && isQueueSelected}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedStartDate={setSelectedStartDate}
        setSelectedEndDate={setSelectedEndDate}
        name="Отчет по услугам"
        filterContent={
          <div className={classes.filtersRow}>
            <BranchQueueFilter
              selectedBranchIds={filterBranchIds}
              selectedQueueIds={filterQueueIds}
              onBranchChange={setFilterBranchIds}
              onQueueChange={setFilterQueueIds}
              singleQueue
              queueIdsFilter={queueIdsFilter}
              compact
            />

            <label className={classes.serviceFilter}>
              <span className={classes.serviceFilterLabel}>Услуга</span>
              <ReactSelect<ServiceOption, false>
                className={classes.serviceFilterSelect}
                placeholder="Все услуги"
                noOptionsMessage={() => 'Нет услуг'}
                isDisabled={!isQueueSelected || isLoadingServiceReport}
                isClearable
                value={selectedServiceOption}
                options={serviceOptions}
                onChange={(opt) => setSelectedServiceName(opt?.value ?? '')}
                styles={selectStyles}
                menuPlacement="bottom"
                menuPosition="fixed"
                menuPortalTarget={menuPortalTarget}
                maxMenuHeight={240}
              />
            </label>
          </div>
        }
      />

      {!isQueueSelected ? (
        <div className={classes.emptyState}>
          Выберите очередь в фильтре, чтобы отобразить данные
        </div>
      ) : (
        <ServicesReportTable
          serviceReportData={filteredServiceReportData}
          isLoadingData={isLoadingServiceReport}
        />
      )}
    </div>
  );
};

export default ServicesPage;
